// client/src/components/FloatingCart.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';
import { useFloatingCart } from './FloatingCartContext';
import { FaTimes, FaShoppingCart, FaTrash, FaArrowRight } from 'react-icons/fa';
import { API_URL, getImageUrl } from '../config';
import './FloatingCart.css';

const FloatingCart = () => {
  const { cart = [], removeFromCart, updateQuantity } = useCart();
  const { isOpen, toggleCart, closeCart } = useFloatingCart();
  const navigate = useNavigate();
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(750);

  // Fetch free shipping threshold from site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/site-settings`);
        if (response.ok) {
          const settings = await response.json();
          const threshold = settings.shipping?.freeShippingThreshold || 750;
          setFreeShippingThreshold(threshold);
        }
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
        // Keep default if fetch fails
        setFreeShippingThreshold(750);
      }
    };

    fetchSettings();
  }, []);

  // Calculate totals
  const subtotal = (cart || []).reduce((sum, item) => sum + item.price * item.quantity, 0);
  const amountToFreeDelivery = Math.max(0, freeShippingThreshold - subtotal);
  const hasFreeDelivery = subtotal >= freeShippingThreshold;
  const progressPercentage = Math.min((subtotal / freeShippingThreshold) * 100, 100);

  const handleViewCart = useCallback(() => {
    closeCart();
    navigate('/cart');
  }, [closeCart, navigate]);

  const handleRemove = useCallback((itemId) => {
    removeFromCart(itemId);
  }, [removeFromCart]);

  const handleQuantityChange = useCallback((itemId, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(itemId, newQuantity);
    } else if (newQuantity <= 0) {
      removeFromCart(itemId);
    }
  }, [updateQuantity, removeFromCart, cart]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="floating-cart-overlay" onClick={closeCart} />
      )}

      {/* Cart Sidebar */}
      <div className={`floating-cart-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="floating-cart-header">
          <h5>Shopping Cart</h5>
          <button
            type="button"
            className="close-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeCart();
            }}
            aria-label="Close cart"
          >
            <FaTimes />
          </button>
        </div>

        {/* Free Delivery Progress Bar */}
        <div className="free-delivery-section">
          <div className="progress-info">
            {hasFreeDelivery ? (
              <>
                <div className="progress-text">
                  <strong style={{ color: '#28a745' }}>✓ FREE DELIVERY!</strong>
                </div>
                <small>You've unlocked free shipping on your order</small>
              </>
            ) : (
              <>
                <div className="progress-text">
                  <strong>Free Delivery Over £{freeShippingThreshold}</strong>
                </div>
                <small>
                  Spend <strong>£{amountToFreeDelivery.toFixed(2)}</strong> more for free delivery
                </small>
              </>
            )}
          </div>
          <div className="progress-bar-container">
            <div
              className={`progress-bar-fill ${hasFreeDelivery ? 'complete' : ''}`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="progress-percentage">
            {Math.round(progressPercentage)}%
          </div>
        </div>

        {/* Cart Items */}
        <div className="floating-cart-items">
          {cart.length === 0 ? (
            <div className="empty-cart">
              <FaShoppingCart size={40} />
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="items-list">
              {cart.map((item) => (
                <div key={item._id || item.id} className="cart-item">
                  <div className="item-image">
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      onError={(e) => {
                        e.target.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect width="60" height="60" fill="%23e9ecef"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="12" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  <div className="item-details">
                    <h6>{item.name}</h6>
                    {item.cascadingTier1 && (
                      <small className="text-muted d-block mb-1">
                        {item.cascadingConfig?.tier1Name || 'Tier 1'}: {item.cascadingTier1}
                        {item.cascadingTier2 && (
                          <> | {item.cascadingConfig?.tier2Name || 'Tier 2'}: {item.cascadingTier2}</>
                        )}
                      </small>
                    )}
                    <p className="item-price">£{item.price.toFixed(2)}</p>
                    <div className="quantity-control">
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleQuantityChange(item.variantId || item._id || item.id, item.quantity - 1);
                        }}
                      >
                        −
                      </button>
                      <span className="qty-value">{item.quantity}</span>
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleQuantityChange(item.variantId || item._id || item.id, item.quantity + 1);
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(item.variantId || item._id || item.id);
                    }}
                    title="Remove item"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Footer */}
        {cart.length > 0 && (
          <div className="floating-cart-footer">
            <div className="totals">
              <div className="total-row">
                <span>Subtotal:</span>
                <strong>£{subtotal.toFixed(2)}</strong>
              </div>
              <div className="total-row shipping-row">
                <span>Shipping:</span>
                <strong className={hasFreeDelivery ? 'free' : ''}>
                  {hasFreeDelivery ? (
                    <span style={{ color: '#28a745' }}>FREE</span>
                  ) : (
                    'Calculated at checkout'
                  )}
                </strong>
              </div>
            </div>

            <button
              type="button"
              className="view-cart-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleViewCart();
              }}
            >
              View Full Cart
              <FaArrowRight className="ms-2" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingCart;
